import { ReadActionKey } from 'owa-outlook-service-options';
import { FilterableMenuItem } from 'owa-filterable-menu';
import loc from 'owa-localize';
import * as clickHandlers from '../handlers/onClickHandlers';
import { stopIgnoring } from 'owa-locstrings/lib/strings/stopignoring.locstring.json';
import { ignore } from 'owa-locstrings/lib/strings/ignore.locstring.json';
import { unpin } from 'owa-locstrings/lib/strings/unpin.locstring.json';
import { snooze } from 'owa-locstrings/lib/strings/snooze.locstring.json';
import { categoryMenuItemMenuText } from 'owa-locstrings/lib/strings/categorymenuitemmenutext.locstring.json';
import {
    copy,
    openInNewTab,
    findEmailFromSender,
    advancedMailActions,
    securityOptions,
    view,
} from '../components/getFilterableMailListContextMenuItems.locstring.json';
import { unflag } from 'owa-locstrings/lib/strings/unflag.locstring.json';
import { block } from 'owa-locstrings/lib/strings/block.locstring.json';
import { move } from 'owa-locstrings/lib/strings/move.locstring.json';
import { restoreItem } from 'owa-locstrings/lib/strings/restoreitem.locstring.json';
import { markAsNotJunk, markAsJunk, createTask } from '../strings.locstring.json';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { flag } from 'owa-locstrings/lib/strings/flag.locstring.json';
import { pin } from 'owa-locstrings/lib/strings/pin.locstring.json';
import { archive } from 'owa-locstrings/lib/strings/archive.locstring.json';
import { phishing } from 'owa-locstrings/lib/strings/phishing.locstring.json';
import { deleteItem } from 'owa-locstrings/lib/strings/deleteitem.locstring.json';
import { showInImmersiveReader as showInImmersiveReader_1 } from 'owa-locstrings/lib/strings/showinimmersivereader.locstring.json';
import { itemHeaderTranslate } from 'owa-locstrings/lib/strings/itemheadertranslate.locstring.json';
import { createRule } from 'owa-locstrings/lib/strings/createrule.locstring.json';
import { assignPolicyMenuItemName } from 'owa-locstrings/lib/strings/assignpolicymenuitemname.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import { reportAbuse } from 'owa-locstrings/lib/strings/reportabuse.locstring.json';
import { itemHeaderForward } from 'owa-locstrings/lib/strings/itemheaderforward.locstring.json';
import { itemHeaderReplyAll } from 'owa-locstrings/lib/strings/itemheaderreplyall.locstring.json';
import { itemMailListHeaderViewMessageDetails } from '../components/itemheaderviewmessagedetails.locstring.json';
import { itemMailListHeaderViewMessageSource } from '../components/itemheaderviewmessagesource.locstring.json';
import { itemHeaderReply } from 'owa-locstrings/lib/strings/itemheaderreply.locstring.json';
import { getMailMenuItemShouldShowMap, MenuItemType } from 'owa-mail-filterable-menu-behavior';

import { lazyGetAssignPolicyPropertiesForMailContextMenu } from 'owa-mail-retention-policies';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import * as lazyTriageActions from 'owa-mail-triage-action';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import getSnoozeDateOptions from 'owa-mail-datepicker/lib/utils/getSnoozeDateOptions';
import createSnoozeSubMenu from 'owa-mail-datepicker/lib/utils/createSnoozeSubMenu';
import { getCategoryMenuPropertiesForContextMenu } from 'owa-mail-category-view';
import { getCopyToProperties, getMoveToPropertiesForContextMenu } from 'owa-mail-movetofolder-view';

import { listViewStore, TableView } from 'owa-mail-list-store';

let shouldShowBehaviorMap = getMailMenuItemShouldShowMap();
const contextMenuActionSource = 'ContextMenu';
// Used as a key for divider, because IContextualMenuItem requires each element to have a different key
let dividerKey = 0;

export const updateContextMenuItem: Partial<
    Record<
        ReadActionKey,
        (
            rowKeys: string[],
            tableViewId: string,
            onDismiss: (ev?: any) => void,
            filterableMenuItem: FilterableMenuItem
        ) => void
    >
> = {
    Reply: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(itemHeaderReply);
        filterableMenuItem.onClick = () => clickHandlers.onReplyRow();
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Reply];
    },

    ReplyAll: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(itemHeaderReplyAll);
        filterableMenuItem.onClick = () => clickHandlers.onReplyAllRow();
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.ReplyAll];
    },

    Forward: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(itemHeaderForward);
        filterableMenuItem.onClick = () => clickHandlers.onForwardRow();
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Forward];
    },

    Delete: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(deleteItem);
        filterableMenuItem.onClick = () => clickHandlers.onDeleteClicked(rowKeys, tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Delete];
    },

    Restore: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(restoreItem);
        filterableMenuItem.onClick = () =>
            lazyTriageActions.lazyRestoreMailListRowsAction.importAndExecute(rowKeys, tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Restore];
    },

    Archive: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(archive);
        filterableMenuItem.onClick = () => clickHandlers.onArchiveClicked();
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Archive];
    },

    Move: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(move);
        filterableMenuItem.disabled = false;
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Move];
        filterableMenuItem.subMenuProps = getMoveToPropertiesForContextMenu(
            tableViewId,
            onDismiss,
            contextMenuActionSource,
            shouldShowBehaviorMap,
            DirectionalHint.rightTopEdge
        );
    },

    Copy: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(copy);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Copy];
        filterableMenuItem.disabled = false;
        filterableMenuItem.subMenuProps = getCopyToProperties(
            tableViewId,
            onDismiss,
            contextMenuActionSource
        );
    },

    Categorize: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.disabled = false;
        filterableMenuItem.name = loc(categoryMenuItemMenuText);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Categories];
        filterableMenuItem.subMenuProps = getCategoryMenuPropertiesForContextMenu(
            tableViewId,
            onDismiss,
            'ContextLV',
            true /* shouldShowSearch */
        );
    },

    EmptyFolder: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(categoryMenuItemMenuText);
        filterableMenuItem.onClick = () => {
            const tableView = getTableView(tableViewId);
            lazyTriageActions.lazyEmptyTableView.importAndExecute(
                tableView,
                false /*isExplicitSoftDelete*/,
                'ContextLV',
                tableView.virtualSelectAllExclusionList
            );
        };
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.EmptyFolder];
    },

    MarkAsRead: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(markAsRead);
        filterableMenuItem.onClick = () =>
            clickHandlers.onMarkAsReadUnreadClicked(tableViewId, true /* isReadValueSet */);

        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.MarkAsRead];
    },

    MarkAsUnread: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(markAsUnread);
        filterableMenuItem.onClick = () =>
            clickHandlers.onMarkAsReadUnreadClicked(tableViewId, false /* isReadValueSet */);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.MarkAsUnread];
    },

    Snooze: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        const snoozeDateOptions = getSnoozeDateOptions();
        filterableMenuItem.name = loc(snooze);
        filterableMenuItem.disabled = false;
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Schedule];
        filterableMenuItem.onClick = () => {};
        filterableMenuItem.subMenuProps = createSnoozeSubMenu(
            clickHandlers.onSnoozeClicked,
            snoozeDateOptions,
            DirectionalHint.rightTopEdge
        );
    },

    Flag: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(flag);
        filterableMenuItem.onClick = () => clickHandlers.markRowsAsFlagged();
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Flag];
    },

    Unflag: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(unflag);
        filterableMenuItem.onClick = () => clickHandlers.markRowsAsNotFlagged();
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Unflag];
    },

    Pin: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(pin);
        filterableMenuItem.onClick = () =>
            clickHandlers.markRowsPinnedUnpinned(
                rowKeys,
                tableViewId,
                true /* shouldMarkAsPinned */
            );
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Pin];
    },

    Unpin: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(unpin);
        filterableMenuItem.onClick = () =>
            clickHandlers.markRowsPinnedUnpinned(
                rowKeys,
                tableViewId,
                false /* shouldMarkAsPinned */
            );
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Unpin];
    },

    CreateTask: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(createTask);
        filterableMenuItem.onClick = () => clickHandlers.openTasksView(rowKeys[0], tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.CreateTask];
    },

    MarkAsJunk: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(markAsJunk);
        filterableMenuItem.onClick = () =>
            clickHandlers.onJunkNotJunkClicked(rowKeys, tableViewId, true /* isJunkValueToSet */);

        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.MarkAsJunk];
    },

    MarkAsNotJunk: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(markAsNotJunk);
        filterableMenuItem.onClick = () =>
            clickHandlers.onJunkNotJunkClicked(rowKeys, tableViewId, false /* isJunkValueToSet */);

        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.JunkEmailSafetyAction];
    },

    Ignore: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        const tableView = getTableView(tableViewId);
        filterableMenuItem.name = loc(ignore);
        filterableMenuItem.onClick = () =>
            clickHandlers.onIgnoreStopIgnoreClicked(rowKeys, tableView, true);

        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Ignore];
    },

    ReportAbuse: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(reportAbuse);
        filterableMenuItem.onClick = () =>
            clickHandlers.onReportAbuseClicked(rowKeys[0], tableViewId);

        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.ReportAbuse];
    },

    StopIgnoring: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        const tableView = getTableView(tableViewId);
        filterableMenuItem.name = loc(stopIgnoring);
        filterableMenuItem.onClick = () =>
            clickHandlers.onIgnoreStopIgnoreClicked(rowKeys, tableView, false);

        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.StopIgnoring];
    },

    Block: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(block);
        filterableMenuItem.onClick = () => clickHandlers.onBlockClicked(rowKeys, tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Block];
    },

    FindEmailFromSender: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(findEmailFromSender);
        filterableMenuItem.onClick = () =>
            clickHandlers.onFindEmailFromSenderClicked(rowKeys[0], tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.FindEmailFromSender];
    },

    CreateRule: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(createRule);
        filterableMenuItem.onClick = () =>
            clickHandlers.onCreateRuleClicked(rowKeys[0], tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.InboxRule];
    },

    Translate: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(itemHeaderTranslate);
        filterableMenuItem.onClick = () => clickHandlers.onTranslateClicked(rowKeys, tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Translate];
    },

    ShowInImmersiveReader: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(showInImmersiveReader_1);
        filterableMenuItem.onClick = () =>
            clickHandlers.onShowInImmersiveReaderClicked(rowKeys[0], tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.ShowInImmersiveReader];
    },

    ViewMessageSource: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        const name = isConsumer()
            ? loc(itemMailListHeaderViewMessageSource)
            : loc(itemMailListHeaderViewMessageDetails);

        filterableMenuItem.name = name;
        filterableMenuItem.onClick = () =>
            clickHandlers.onViewMessageDetails(rowKeys[0], tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.ViewMessageDetails];
    },

    OpenInNewTab: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(openInNewTab);
        filterableMenuItem.onClick = () =>
            clickHandlers.onOpenInNewTabClicked(rowKeys, tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.OpenInNewTab];
    },

    AssignPolicy: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        const getAssignPolicyMenu = lazyGetAssignPolicyPropertiesForMailContextMenu.tryImportForRender();
        let assignPolicyMenuItem;
        if (getAssignPolicyMenu) {
            assignPolicyMenuItem = getAssignPolicyMenu(tableViewId, 'ContextLV' /* actionSource */);
        }
        filterableMenuItem.disabled = false;
        filterableMenuItem.name = loc(assignPolicyMenuItemName);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.AssignPolicy];
        filterableMenuItem.subMenuProps = assignPolicyMenuItem;
    },

    '-': (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = '-';
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.Reply];
        filterableMenuItem.key = (dividerKey++).toString();
    },

    MarkAsPhishing: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(phishing);
        filterableMenuItem.onClick = () =>
            clickHandlers.onMarkAsPhishingClicked(rowKeys, tableViewId);
        filterableMenuItem.shouldShow = shouldShowBehaviorMap[MenuItemType.MarkAsPhishing]; // Report Phishing will be shown alongside Mark as Junk
    },
    View: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(view);
        filterableMenuItem.shouldShow = () =>
            shouldShowBehaviorMap[MenuItemType.OpenInNewTab]() ||
            shouldShowBehaviorMap[MenuItemType.ShowInImmersiveReader]() ||
            shouldShowBehaviorMap[MenuItemType.Translate]() ||
            shouldShowBehaviorMap[MenuItemType.ViewMessageDetails]();
        filterableMenuItem.subMenuKeys = [
            'OpenInNewTab',
            'ShowInImmersiveReader',
            'Translate',
            'ViewMessageSource',
        ];
    },
    MessageSafety: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(securityOptions);
        filterableMenuItem.shouldShow = () =>
            shouldShowBehaviorMap[MenuItemType.MarkAsJunk]() ||
            shouldShowBehaviorMap[MenuItemType.JunkEmailSafetyAction]() ||
            shouldShowBehaviorMap[MenuItemType.ReportAbuse]() ||
            shouldShowBehaviorMap[MenuItemType.Block]() ||
            shouldShowBehaviorMap[MenuItemType.MarkAsPhishing]();

        filterableMenuItem.subMenuKeys = [
            'MarkAsJunk',
            'MarkAsNotJunk',
            'ReportAbuse',
            'Block',
            'MarkAsPhishing',
        ];
    },
    AdvancedActions: (
        rowKeys: string[],
        tableViewId: string,
        onDismiss: (ev?: any) => void,
        filterableMenuItem: FilterableMenuItem
    ) => {
        filterableMenuItem.name = loc(advancedMailActions);

        filterableMenuItem.shouldShow = () =>
            shouldShowBehaviorMap[MenuItemType.CreateTask]() ||
            shouldShowBehaviorMap[MenuItemType.InboxRule]() ||
            shouldShowBehaviorMap[MenuItemType.AssignPolicy]();
        filterableMenuItem.subMenuKeys = [
            'CreateRule',
            'AssignPolicy',
            'CreateTask',
            'FindEmailFromSender',
        ];
    },
};

function getTableView(tableViewId: string): TableView {
    return listViewStore.tableViews.get(tableViewId);
}
